import { useFlag, useNewCode } from '@calendly/feature-check-react'
import { useOnValueChange } from '@calendly/hooks'
import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { useFetchTrackedMeetingWithSummariesByForeignIdQuery } from 'hooks/tracked_meetings/api'
import { timezoneSelector } from 'selectors/current_user'

import {
  hasPreloadedDataForEvent,
  shouldLoadMeetingIntelligence,
  isMatchingMeeting,
  buildMeetingWithIntelligence,
  calculateLoadingState,
  defensiveMergeMeeting,
} from './helpers'

/**
 * Pure hook for managing active meeting state.
 * Accepts dependencies as parameters to allow different features to use their own data sources.
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.selectedView - Selected view ('scheduled' or 'pending')
 * @param {string} params.scopeType - Scope type
 * @param {string} params.scopeId - Scope ID
 * @param {string} params.eventId - Event ID
 * @param {string} params.shareId - Share ID
 * @param {boolean} params.skipTrackedMeetingCall - Skip tracked meeting call
 * @param {Object} params.preloadedMeetingData - Preloaded meeting data
 * @param {Function} params.useLazyGetMeetingQuery - Hook for lazy getting meeting (from REST API)
 * @param {Function} params.useLazyGetMeetingFromSearchQuery - Hook for lazy getting meeting from search (from GraphQL)
 * @param {Function} params.isPendingEvent - Function to check if event is pending
 * @param {Function} params.mapNodeToMeeting - Function to map node to meeting object
 * @param {Object} params.currentTime - Current time moment object for time synchronization (optional)
 * @param {boolean} params.skipLazyFetchWhenProviderFetches - When true, do not trigger lazy REST getMeeting in
 *   scheduled view (provider is already fetching)
 * @param {boolean} params.supergraphMeetingDetailEnabled - When true, detail comes from Supergraph;
 *   never call tracked_meetings (with-summaries) for merged intelligence
 * @returns {Object} Active meeting state and handlers
 */
export const useActiveMeeting = ({
  selectedView,
  scopeType,
  scopeId,
  eventId,
  shareId,
  skipTrackedMeetingCall = false,
  preloadedMeetingData,
  useLazyGetMeetingQuery,
  useLazyGetMeetingFromSearchQuery,
  isPendingEvent,
  mapNodeToMeeting,
  currentTime,
  skipLazyFetchWhenProviderFetches = false,
  supergraphMeetingDetailEnabled = false,
}) => {
  const meetingIntelligenceEnabled = useNewCode('meeting_intelligence')?.enabled
  const hasMsTeamsFlag = useFlag('ms_teams_notetaker')
  const history = useHistory()
  const timezone = useSelector(timezoneSelector)

  const [trigger, { data, error: mainError }] = useLazyGetMeetingQuery()
  const [triggerSearch, { data: searchData, error: searchError }] = useLazyGetMeetingFromSearchQuery()
  // Only holds data from this hook's own API calls (REST, search, intelligence). Preloaded data is merged in useMemo.
  const [fetchedMeetingData, setFetchedMeetingData] = useState(null)
  const processedEventIdRef = useRef(null)
  const shouldSkipSearchRef = useRef(false)
  const searchTimeoutRef = useRef(null)

  // Single reference for "current event's meeting" (preloaded or fetched) for intelligence loading and hasMeetingData.
  const referenceMeeting = useMemo(() => {
    if (hasPreloadedDataForEvent(preloadedMeetingData, eventId)) return preloadedMeetingData
    if (fetchedMeetingData?.uuid === eventId) return fetchedMeetingData
    return null
  }, [preloadedMeetingData, eventId, fetchedMeetingData])

  const shouldLoadMeetingIntelligenceData = shouldLoadMeetingIntelligence(
    referenceMeeting,
    meetingIntelligenceEnabled,
    isPendingEvent,
    skipTrackedMeetingCall
  ) && !supergraphMeetingDetailEnabled

  const { data: meetingIntelligenceData } = useFetchTrackedMeetingWithSummariesByForeignIdQuery(
    referenceMeeting?.uuid,
    { skip: !shouldLoadMeetingIntelligenceData }
  )

  const clearActiveMeeting = useCallback(() => {
    setFetchedMeetingData(null)
    const params = new URLSearchParams(history.location.search)
    params.delete('selected')
    params.delete('share_id')
    history.replace({ search: params.toString() })
  }, [history])

  useOnValueChange(searchData, () => {
    if (!searchData) return

    if (searchData.kind === 'CALENDLY') {
      trigger({ eventId: searchData.uuid, scopeType, scopeId, selectedView, _t: Date.now() })
    } else {
      // Pass currentTime if provided to ensure time synchronization with CurrentTimeContext
      // If not provided, mapNodeToMeeting will use its default (moment())
      const newMeeting = currentTime
        ? mapNodeToMeeting(searchData, timezone, hasMsTeamsFlag, currentTime)
        : mapNodeToMeeting(searchData, timezone, hasMsTeamsFlag)
      // Non-CALENDLY meetings: use defensive merge to preserve existing fields
      setFetchedMeetingData((prev) => {
        if (!prev || prev.uuid !== newMeeting.uuid) return newMeeting
        return defensiveMergeMeeting(prev, newMeeting, { preserveMeetingIntelligence: false })
      })
    }
  })

  const updateActiveMeeting = useCallback(() => {
    const triggerParams = { scopeType, scopeId, selectedView, _t: Date.now() }

    if (shareId && selectedView === 'pending') {
      trigger({ ...triggerParams, shareId })
    } else if (eventId) {
      trigger({ ...triggerParams, eventId })
    }
  }, [eventId, trigger, scopeType, scopeId, selectedView, shareId])

  // Track when we should skip search based on preloaded data
  useEffect(() => {
    const eventIdChanged = processedEventIdRef.current !== eventId
    const hasPreloadedData = hasPreloadedDataForEvent(preloadedMeetingData, eventId)

    if (eventIdChanged) {
      shouldSkipSearchRef.current = false
      processedEventIdRef.current = null
    }

    if (hasPreloadedData) {
      shouldSkipSearchRef.current = true
      processedEventIdRef.current = eventId
    }
  }, [preloadedMeetingData, eventId])

  // Handle fetching meeting data
  useEffect(() => {
    const clearSearchTimeout = () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
        searchTimeoutRef.current = null
      }
    }

    const resetRefsForNewEvent = () => {
      if (processedEventIdRef.current !== eventId) {
        processedEventIdRef.current = null
        shouldSkipSearchRef.current = false
      }
    }

    const markPreloadedDataAsProcessed = () => {
      if (hasPreloadedDataForEvent(preloadedMeetingData, eventId)) {
        shouldSkipSearchRef.current = true
        processedEventIdRef.current = eventId
      }
    }

    const triggerWithParams = (params) => {
      trigger({ ...params, scopeType, scopeId, selectedView, _t: Date.now() })
    }

    const handleScheduledViewTrigger = () => {
      if (!scopeType || !scopeId) return

      searchTimeoutRef.current = setTimeout(() => {
        if (!shouldSkipSearchRef.current && processedEventIdRef.current !== eventId) {
          triggerWithParams({ eventId })
          processedEventIdRef.current = eventId
        }
      }, 100) // Small delay to allow cache to populate
    }

    const handlePendingViewTrigger = () => {
      if (shareId) {
        triggerWithParams({ shareId })
        processedEventIdRef.current = eventId || shareId
      } else if (eventId) {
        triggerWithParams({ eventId })
        processedEventIdRef.current = eventId
      }
    }

    const shouldFetchMeeting = () => (
      !shouldSkipSearchRef.current
        && processedEventIdRef.current !== eventId
        && (eventId || shareId)
    )

    // Clear any pending search timeout
    clearSearchTimeout()

    // Reset processed ref when eventId changes
    resetRefsForNewEvent()

    // If we have preloaded meeting data that matches the eventId, skip fetching (provider already runs REST + merge for old/realtime)
    const hasPreloadedData = hasPreloadedDataForEvent(preloadedMeetingData, eventId)
    if (hasPreloadedData) {
      markPreloadedDataAsProcessed()
    } else if (shouldFetchMeeting()) {
      const isScheduledView = selectedView !== 'pending'
      const isPendingView = selectedView === 'pending'

      // Trigger REST fetch when we don't have matching preloaded data (including stale preloaded data for a different eventId).
      // When skipLazyFetchWhenProviderFetches is true, the provider (e.g. MeetingDetailProvider) is already running
      // useGetMeetingQuery; avoid duplicate getMeeting calls by not scheduling the lazy trigger here.
      if (isScheduledView && eventId && !skipLazyFetchWhenProviderFetches) {
        handleScheduledViewTrigger()
      } else if (isPendingView) {
        handlePendingViewTrigger()
      }
    }

    // Cleanup timeout on unmount or when dependencies change
    return clearSearchTimeout
  },
  [
    eventId,
    trigger,
    triggerSearch,
    scopeType,
    scopeId,
    selectedView,
    shareId,
    preloadedMeetingData,
    skipLazyFetchWhenProviderFetches,
  ])

  // Always merge REST data when it arrives so rich data (full invitee, etc.) gets into state.
  // Do not skip when preloaded exists: preloaded may be cache-only (thin); merging REST ensures we keep rich data.
  useEffect(() => {
    if (data && isMatchingMeeting(data, eventId, shareId, selectedView)) {
      const meetingWithIntelligence = buildMeetingWithIntelligence(
        data,
        searchData,
        meetingIntelligenceData
      )
      // DEFENSIVE MERGE: Preserve key detail fields if they're missing in the REST API update
      // preserveMeetingIntelligence: false because meetingWithIntelligence already has the correct intelligence
      setFetchedMeetingData((prev) => defensiveMergeMeeting(prev, meetingWithIntelligence, { preserveMeetingIntelligence: false }))
    }
  }, [
    data,
    mainError,
    eventId,
    meetingIntelligenceEnabled,
    selectedView,
    shareId,
    meetingIntelligenceData,
    searchData,
  ])

  // If we have preloaded data, we're not loading
  // If searchError is undefined (no-op hook), don't wait for search - only wait for mainError
  const hasMeetingData = !!referenceMeeting
  const isLoadingState = calculateLoadingState(
    hasMeetingData,
    mainError,
    searchError,
    eventId,
    shareId
  )

  // Single merge point: preloaded (cache) + fetched (REST/search/intelligence). One merge per cache poll.
  // - preloadedMeetingData can be cache-only (thin) or already merged (realtime).
  // - fetchedMeetingData is only set by this hook's API calls (REST, search, intelligence).
  // When both exist, merge with preloaded as prev and fetched as newData so REST wins for location fields.
  // preserveMeetingIntelligence: true keeps preloaded in merged; we then apply meetingIntelligenceData from the query below.
  // IMPORTANT: This is computed BEFORE the early return so hosts are preserved during intelligence loading.
  // Apply meetingIntelligenceData here so we never lose the recap/summary regardless of merge order or timing of useOnValueChange.
  const activeMeetingData = useMemo(() => {
    const hasPreloaded = hasPreloadedDataForEvent(preloadedMeetingData, eventId)
    const hasFetchedMatch = fetchedMeetingData && fetchedMeetingData.uuid === eventId

    let merged
    if (hasPreloaded && hasFetchedMatch) {
      merged = defensiveMergeMeeting(preloadedMeetingData, fetchedMeetingData, { preserveMeetingIntelligence: true })
    } else if (hasPreloaded) {
      merged = preloadedMeetingData
    } else {
      merged = fetchedMeetingData
    }

    // Apply tracked-meeting API intelligence only when not using Supergraph for detail (no tracked_meetings calls in that mode).
    if (merged?.uuid === eventId && meetingIntelligenceData && !supergraphMeetingDetailEnabled) {
      return { ...merged, meetingIntelligence: meetingIntelligenceData }
    }
    return merged
  }, [fetchedMeetingData, preloadedMeetingData, eventId, meetingIntelligenceData, supergraphMeetingDetailEnabled])

  // When meeting intelligence is loading, still show merged data to preserve hosts
  // This ensures hosts, location, etc. render immediately while intelligence loads in background
  if (shouldLoadMeetingIntelligenceData && !meetingIntelligenceData) {
    return {
      activeMeeting: activeMeetingData,
      clearActiveMeeting,
      updateActiveMeeting,
      isLoading: !hasMeetingData && (Boolean(eventId) || Boolean(shareId))
    }
  }

  return {
    activeMeeting: activeMeetingData,
    clearActiveMeeting,
    updateActiveMeeting,
    isLoading: isLoadingState
  }
}
